import {
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react"
import { createPortal } from "react-dom"
import { Check, ChevronsUpDown, Search } from "lucide-react"

import { cn } from "../../lib/utils"
import { PortalContainerContext } from "./portal-container"
import { useFloatingPanelPosition } from "./use-floating-panel-position"

export type ComboboxOption = {
  value: string
  label: string
  /** Optional leading visual, e.g. a marketplace logo or a user glyph. May hold
   *  several elements (flag + logo) — they lay out in a row. */
  icon?: ReactNode | undefined
  /** Extra text folded into the search match (provider, aliases, raw id…). */
  keywords?: string | undefined
  /** Keep a current or historical value visible without allowing selection. */
  disabled?: boolean | undefined
}

type BaseProps = {
  options: readonly ComboboxOption[]
  /** Trigger text when nothing is selected. */
  placeholder?: string | undefined
  /** Search box placeholder. */
  searchPlaceholder?: string | undefined
  /** Label of the top "reset to no selection" row. Omit to hide the row. */
  clearLabel?: string | undefined
  emptyMessage?: string | undefined
  disabled?: boolean | undefined
  className?: string | undefined
  triggerClassName?: string | undefined
  size?: "sm" | "md" | undefined
  /** Opt-in 44px controls and menu options for touch-first pages. */
  touchFriendly?: boolean
  /** Noun used for a multi-selection summary. */
  selectionLabel?: string
  id?: string | undefined
  "aria-label"?: string | undefined
  "aria-describedby"?: string | undefined
  "aria-invalid"?: boolean | undefined
  title?: string | undefined
}

/** Single-select: commits one value and closes on pick. */
type SingleProps = BaseProps & {
  multiple?: false
  value: string | undefined
  onChange: (value: string | undefined) => void
}

/** Multi-select: toggles values and stays open; checkmarks every selected row. */
type MultiProps = BaseProps & {
  multiple: true
  value: readonly string[]
  onChange: (value: string[]) => void
}

type ComboboxProps = SingleProps | MultiProps

// Normalize either mode's selection to a plain array for shared rendering.
function selectionOf(props: ComboboxProps): readonly string[] {
  if (props.multiple) {
    return props.value
  }
  return props.value !== undefined && props.value !== "" ? [props.value] : []
}

function matches(option: ComboboxOption, query: string): boolean {
  if (!query) {
    return true
  }
  const haystack = `${option.label} ${option.value} ${option.keywords ?? ""}`.toLowerCase()
  return haystack.includes(query.toLowerCase())
}

function openComboboxWithArrowDown(
  event: React.KeyboardEvent<HTMLButtonElement>,
  open: boolean,
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
) {
  if (!open && event.key === "ArrowDown") {
    event.preventDefault()
    setOpen(true)
  }
}

function portalWhenOpen(open: boolean, panel: ReactNode, container: HTMLElement | null): ReactNode {
  if (!open || typeof document === "undefined") {
    return null
  }
  return createPortal(panel, container ?? document.body)
}

function touchControlClass(touchFriendly: boolean): string | undefined {
  return touchFriendly ? "min-h-11" : undefined
}

export function Combobox(props: ComboboxProps) {
  const portalContainer = useContext(PortalContainerContext)
  const {
    options,
    placeholder = "Select…",
    searchPlaceholder = "Search…",
    clearLabel,
    emptyMessage = "No matches",
    disabled = false,
    className,
    size = "md",
    touchFriendly = false,
    id,
    title,
  } = props

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [highlight, setHighlight] = useState(0)

  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const listboxId = useId()
  const panelStyle = useFloatingPanelPosition(open, triggerRef)

  const selectedValues = selectionOf(props)
  const hasSelection = selectedValues.length > 0

  const filtered = options.filter((option) => matches(option, query))

  // Close on outside pointer, reset the transient search/highlight state.
  useEffect(() => {
    if (!open) {
      return
    }
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node
      if (
        rootRef.current &&
        !rootRef.current.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener("pointerdown", onPointerDown)
    return () => document.removeEventListener("pointerdown", onPointerDown)
  }, [open])

  useEffect(() => {
    if (open) {
      searchRef.current?.focus()
    } else {
      setQuery("")
    }
  }, [open])

  // Keep the highlight in range as the filtered set shrinks while typing.
  useEffect(() => {
    setHighlight(0)
  }, [query])

  // Scroll the active option into view during keyboard navigation.
  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-index="${highlight}"]`)
      ?.scrollIntoView({ block: "nearest" })
  }, [highlight])

  // Single-select commits and closes; multi-select toggles and stays open so
  // the operator can pick several without reopening.
  function closeAndFocusTrigger() {
    setOpen(false)
    queueMicrotask(() => triggerRef.current?.focus())
  }

  function pick(value: string) {
    if (options.find((option) => option.value === value)?.disabled) {
      return
    }
    if (props.multiple) {
      const next = props.value.includes(value)
        ? props.value.filter((v) => v !== value)
        : [...props.value, value]
      props.onChange(next)
      searchRef.current?.focus()
    } else {
      props.onChange(value)
      closeAndFocusTrigger()
    }
  }

  function clear() {
    if (props.multiple) {
      props.onChange([])
    } else {
      props.onChange(undefined)
    }
    closeAndFocusTrigger()
  }

  function onSearchKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault()
      setHighlight((index) => Math.min(index + 1, filtered.length - 1))
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      setHighlight((index) => Math.max(index - 1, 0))
    } else if (event.key === "Enter") {
      event.preventDefault()
      const option = filtered[highlight]
      if (option) {
        pick(option.value)
      }
    } else if (event.key === "Escape") {
      event.preventDefault()
      closeAndFocusTrigger()
    }
  }

  const trigger = triggerContent(
    props.multiple === true,
    selectedValues,
    options,
    placeholder,
    props.selectionLabel
  )
  const selectedValue = props.multiple ? props.value.join(",") : (props.value ?? "")

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        role="combobox"
        value={selectedValue}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={
          open && filtered[highlight] ? `${listboxId}-option-${highlight}` : undefined
        }
        aria-autocomplete="list"
        aria-label={props["aria-label"]}
        aria-describedby={props["aria-describedby"]}
        aria-invalid={props["aria-invalid"]}
        title={title}
        onClick={() => setOpen((isOpen) => !isOpen)}
        onKeyDown={(event) => openComboboxWithArrowDown(event, open, setOpen)}
        className={cn(
          "flex w-full items-center gap-2 rounded-full border border-border bg-card px-3.5 text-[13px] shadow-card outline-none transition-[border-color,box-shadow,background-color] duration-200",
          size === "sm" ? "h-8" : "h-9",
          touchControlClass(touchFriendly),
          "hover:border-foreground/15 hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/30",
          "disabled:cursor-not-allowed disabled:opacity-50",
          props.triggerClassName
        )}
      >
        {trigger.icon ? (
          <span className="flex shrink-0 items-center gap-1">{trigger.icon}</span>
        ) : null}

        <span
          className={cn("truncate", trigger.muted ? "text-muted-foreground" : "text-foreground")}
        >
          {trigger.label}
        </span>

        <ChevronsUpDown className="ml-auto size-3.5 shrink-0 text-muted-foreground" />
      </button>

      {portalWhenOpen(
        open,
        <ComboboxPanel
          touchFriendly={touchFriendly}
          ref={panelRef}
          style={panelStyle}
          listboxId={listboxId}
          searchRef={searchRef}
          listRef={listRef}
          query={query}
          onQueryChange={setQuery}
          onKeyDown={onSearchKeyDown}
          searchPlaceholder={searchPlaceholder}
          options={filtered}
          multiple={props.multiple === true}
          selectedValues={selectedValues}
          highlight={highlight}
          onHighlight={setHighlight}
          onPick={pick}
          onClear={clear}
          clearLabel={clearLabel}
          hasSelection={hasSelection}
          emptyMessage={emptyMessage}
        />,
        portalContainer
      )}
    </div>
  )
}

type TriggerContent = { icon: ReactNode | null; label: string; muted: boolean }

// Trigger shows the lone selection (icon + label), a "<n> selected" summary when
// several are chosen, or the muted placeholder when empty.
function triggerContent(
  multiple: boolean,
  selectedValues: readonly string[],
  options: readonly ComboboxOption[],
  placeholder: string,
  selectionLabel = "selected"
): TriggerContent {
  if (selectedValues.length === 0) {
    return { icon: null, label: placeholder, muted: true }
  }
  if (selectedValues.length === 1) {
    const value = selectedValues[0]
    const option = options.find((o) => o.value === value)
    return {
      icon: option?.icon ?? null,
      label: option?.label ?? value ?? placeholder,
      muted: false,
    }
  }
  return { icon: null, label: `${selectedValues.length} ${selectionLabel}`, muted: false }
}

type ComboboxPanelProps = {
  touchFriendly: boolean
  ref: React.RefObject<HTMLDivElement | null>
  style: CSSProperties
  listboxId: string
  searchRef: React.RefObject<HTMLInputElement | null>
  listRef: React.RefObject<HTMLUListElement | null>
  query: string
  onQueryChange: (value: string) => void
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void
  searchPlaceholder: string
  options: readonly ComboboxOption[]
  multiple: boolean
  selectedValues: readonly string[]
  highlight: number
  onHighlight: (index: number) => void
  onPick: (value: string) => void
  onClear: () => void
  clearLabel: string | undefined
  hasSelection: boolean
  emptyMessage: string
}

function ComboboxPanel({
  touchFriendly,
  ref,
  style,
  listboxId,
  searchRef,
  listRef,
  query,
  onQueryChange,
  onKeyDown,
  searchPlaceholder,
  options,
  multiple,
  selectedValues,
  highlight,
  onHighlight,
  onPick,
  onClear,
  clearLabel,
  hasSelection,
  emptyMessage,
}: ComboboxPanelProps) {
  return (
    <div
      ref={ref}
      data-combobox-panel
      style={style}
      className={cn(
        "motion-floating-surface fixed z-50 flex min-w-56 flex-col overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-floating",
        touchFriendly &&
          "[&_button]:min-h-11 [&_input]:min-h-11 [&_input]:text-base [&_input]:placeholder:text-muted-foreground"
      )}
    >
      <div className="flex items-center gap-2 border-b border-border px-3">
        <Search className="size-3.5 shrink-0 text-muted-foreground" />

        <input
          type="search"
          aria-label={searchPlaceholder}
          ref={searchRef}
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder={searchPlaceholder}
          className="h-9 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground/60"
        />
      </div>

      <ul
        id={listboxId}
        ref={listRef}
        role="listbox"
        aria-multiselectable={multiple}
        className="min-h-0 overflow-y-auto p-1"
      >
        {clearLabel ? (
          <li>
            <button
              type="button"
              role="option"
              aria-selected={!hasSelection}
              onClick={onClear}
              className={cn(
                "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground",
                !hasSelection && "text-foreground"
              )}
            >
              <span className="truncate">{clearLabel}</span>

              {!hasSelection ? <Check className="ml-auto size-3.5 text-foreground" /> : null}
            </button>
          </li>
        ) : null}

        {options.length === 0 ? (
          <li className="px-2 py-6 text-center text-sm text-muted-foreground">{emptyMessage}</li>
        ) : (
          options.map((option, index) => (
            <ComboboxRow
              key={option.value}
              listboxId={listboxId}
              option={option}
              index={index}
              active={index === highlight}
              selected={selectedValues.includes(option.value)}
              onHighlight={onHighlight}
              onPick={onPick}
            />
          ))
        )}
      </ul>
    </div>
  )
}

type ComboboxRowProps = {
  listboxId: string
  option: ComboboxOption
  index: number
  active: boolean
  selected: boolean
  onHighlight: (index: number) => void
  onPick: (value: string) => void
}

function ComboboxRow({
  listboxId,
  option,
  index,
  active,
  selected,
  onHighlight,
  onPick,
}: ComboboxRowProps) {
  return (
    <li>
      <button
        id={`${listboxId}-option-${index}`}
        type="button"
        value={option.value}
        role="option"
        aria-selected={selected}
        aria-disabled={option.disabled}
        disabled={option.disabled}
        data-index={index}
        onMouseEnter={() => onHighlight(index)}
        onClick={() => onPick(option.value)}
        className={cn(
          "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors",
          active ? "bg-muted text-foreground" : "text-foreground",
          option.disabled && "cursor-not-allowed opacity-50"
        )}
      >
        {option.icon ? (
          <span className="flex shrink-0 items-center gap-1">{option.icon}</span>
        ) : null}

        <span className="truncate">{option.label}</span>

        {selected ? <Check className="ml-auto size-3.5 shrink-0 text-foreground" /> : null}
      </button>
    </li>
  )
}
